// eslint-disable-next-line max-classes-per-file
import { v4 as uuid } from "uuid";
import Radio from "backbone.radio";
import { capitalize } from "lodash-es";

import { emitter } from "common/emitter";
import { store } from "common/store";

class DynamicPromiseList {
    constructor(options) {
        this.options = options;
        this.#requests = [];
    }
    
    static delay = 500;
    
    #requests = null;
    
    #onCompletePromise = null;
    
    cancelComplete() {
        // Check if there is pending onEmptyPromise
        if (this.#onCompletePromise) {
            this.#onCompletePromise.cancel();
            this.#onCompletePromise = null;
        }
    }
    
    add(request) {
        this.cancelComplete();
    
        // On any status change
        request.isPending = true;
        
        const settle = () => {
            request.isPending = false;
            this.checkCompleteness();
        };
        
        // This is a branch of a returned promise from request and we don't need to return/throw down the chain
        request.then(settle, settle);
        
        this.#requests.push(request);
        
        this.checkCompleteness();
    }
    
    checkCompleteness() {
        // If all items are completed
        if (this.#requests.length && this.#requests.every((item) => !item.isPending)) {
            this.#onCompletePromise = Promise.delay(DynamicPromiseList.delay).then(() => {
                this.options.onComplete(this.#requests.map((request) => ({
                    url: request.url,
                    request_id: request.id,
                    start: request.startTime,
                    end: request.endTime,
                    duration: request.duration
                })));
                
                // Clear the map
                this.#requests = [];
            });
        }
    }
    
    kill() {
        this.cancelComplete();
        this.#requests = [];
    }
}

class PageTracker {
    #groupID = null;
    
    #createdAt = null;
    
    #promiseList = null;
    
    static makePageMeta() {
        const { product, fragment } = Radio.channel("main").request("get:page");
        
        const additionalMeta = {};
        
        if (product === "benchmarks") {
            const state = store.getState();
            Object.assign(additionalMeta, {
                segments: state.benchmarks.segments.length,
                metrics: state.benchmarks.metrics.length,
                view: state.benchmarks.expanded_metric === null ? "all" : "pivot"
            });
        }
        
        if ([
            "authorizations",
            "cashflow",
            "fees",
            "chargebacks",
            "settlements",
            "all_fraud",
            "psd2_fraud"
        ].includes(product)) {
            let view;
            if (fragment.includes("show:transactions")) {
                view = "transactions";
            } else if (fragment.includes("card:all")) {
                view = "all";
            } else if (fragment.includes("view:tablePivot")) {
                view = "pivot";
            } else if (fragment.includes("view:tableInterval")) {
                view = "change-over-time";
            } else {
                // Can be cohort view or a bridge chart
                view = "unknown";
            }
            
            Object.assign(additionalMeta, {
                view
            });
        }
        
        return {
            name: capitalize(product),
            method: product,
            fragment,
            ...additionalMeta
        };
    }
    
    getCurrentRequestGroupID() {
        return this.#groupID || this.makeNewRequestGroupID();
    }
    
    makeNewRequestGroupID() {
        if (this.#promiseList) {
            this.#promiseList.kill();
        }
        this.#groupID = uuid();
        this.#createdAt = Date.now();
        this.#promiseList = new DynamicPromiseList({
            onComplete: (allRequests) => {
                if (!allRequests.length) return;
    
                const startTime = Math.min(...allRequests.map(({ start }) => start)) || this.#createdAt;
                const endTime = Math.max(...allRequests.map(({ end }) => end)) || Date.now() - DynamicPromiseList.delay;
                
                // We should never fail here. Otherwise there will be memory leaks and noise in Sentry
                try {
                    emitter.emit("tracking.track", {
                        type: "Page ready",
                        data: {
                            ...PageTracker.makePageMeta(),
                            start: new Date(startTime).toISOString(),
                            end: new Date(endTime).toISOString(),
                            duration: endTime - startTime,
                            requests_count: allRequests.length,
                            requests: allRequests.map((r) => ({
                                ...r,
                                end: new Date(r.end).toISOString(),
                                start: new Date(r.start).toISOString()
                            }))
                        }
                    });
                } catch (error) {
                    console.error(error);
                }
                
                // Nullify the group
                this.#groupID = null;
            }
        });
        
        return this.#groupID;
    }
    
    track(request) {
        this.#promiseList.add(request);
    }
}

export const pageTracker = new PageTracker();
